import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, X, Bell } from "lucide-react";
import { api, inr } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const navigate = useNavigate();
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const soldOut = product.stock === 0;

  const submitNotify = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }
    setBusy(true);
    try {
      await api.post("/restock-alert", { product_id: product.id, email: email.trim() });
      toast.success("You're on the list — we'll email you when it's back");
      setNotifyOpen(false);
      setEmail("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
        className="group cursor-pointer"
        onClick={() => navigate(`/product/${product.id}`)}
        data-testid={`product-card-${product.id}`}
      >
        <div className="relative overflow-hidden rounded-xl border border-[#d4af37]/15 bg-[#121215] transition-all duration-500 group-hover:border-[#d4af37]/50 group-hover:shadow-[0_20px_60px_rgba(212,175,55,0.12)]">
          <div className="aspect-[4/5] overflow-hidden">
            <img
              src={product.images?.[0]}
              alt={product.name}
              loading="lazy"
              className={`w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.07] ${soldOut ? "opacity-70" : ""}`}
            />
          </div>
          {soldOut && (
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#0A0A0B] via-[#0A0A0B]/85 to-transparent">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNotifyOpen(true);
                }}
                data-testid={`notify-card-${product.id}`}
                className="w-full py-2.5 rounded-full bg-[#d4af37] text-[#0a0a0b] text-xs font-semibold tracking-[0.14em] uppercase flex items-center justify-center gap-2 hover:bg-[#f3e5ab] transition-colors"
              >
                <Bell size={13} /> Notify Me
              </button>
            </div>
          )}
          {!soldOut && (
            <>
              {product.featured && (
                <span className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase bg-[#0a0a0b]/80 backdrop-blur px-3 py-1.5 rounded-full text-[#f3e5ab] border border-[#d4af37]/30">
                  Signature
                </span>
              )}
              {product.stock <= 5 && product.stock > 0 && (
                <span className="absolute top-3 right-3 text-[10px] tracking-[0.16em] uppercase bg-[#0a0a0b]/80 backdrop-blur px-3 py-1.5 rounded-full text-amber-200/90 border border-amber-500/30">
                  Only {product.stock} left
                </span>
              )}
              <button
                data-testid={`quick-add-${product.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  add(product);
                }}
                className="absolute bottom-3 right-3 p-3 rounded-full bg-[#d4af37] text-[#0a0a0b] opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 hover:bg-[#f3e5ab] active:scale-95"
                aria-label={`Add ${product.name} to bag`}
              >
                <Plus size={16} strokeWidth={2.2} />
              </button>
            </>
          )}
        </div>

        <div className="pt-4 pb-1 px-1">
          <p className="text-[10px] tracking-[0.24em] uppercase text-[#d4af37]/70 mb-1.5">{product.category}</p>
          <h3 className="font-serif text-xl leading-snug text-zinc-100 group-hover:text-[#f3e5ab] transition-colors">
            {product.name}
          </h3>
          <p className="mt-1 text-sm text-zinc-400">
            {soldOut ? (
              <span className="text-[11px] tracking-[0.16em] uppercase text-zinc-500" data-testid={`soldout-label-${product.id}`}>Out of stock</span>
            ) : (
              <>
                <span className="text-zinc-100 font-medium">{inr(product.price)}</span>
                {product.mrp > product.price && (
                  <span className="ml-2 line-through text-zinc-600">{inr(product.mrp)}</span>
                )}
              </>
            )}
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {notifyOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center px-5"
            onClick={() => setNotifyOpen(false)}
            data-testid="notify-card-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="w-full max-w-sm rounded-2xl border border-[#d4af37]/30 bg-[#101013] p-8 relative"
              onClick={(e) => e.stopPropagation()}
              data-testid="notify-card-dialog"
            >
              <button
                onClick={() => setNotifyOpen(false)}
                className="absolute top-4 right-4 p-2 rounded-full text-zinc-500 hover:text-zinc-200"
                data-testid="notify-card-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <span className="inline-flex p-3 rounded-full border border-[#d4af37]/35 text-[#d4af37] mb-4">
                <Bell size={18} />
              </span>
              <h3 className="font-serif text-2xl text-zinc-100 leading-snug">{product.name}</h3>
              <p className="text-sm text-zinc-500 mt-2 mb-6">
                Out of stock right now — leave your email and we&rsquo;ll tell you the moment it returns.
              </p>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="input-dark mb-4"
                data-testid="notify-card-email-input"
                aria-label="Email for restock alert"
              />
              <button onClick={submitNotify} disabled={busy} className="btn-gold w-full" data-testid="notify-card-submit">
                {busy ? "Adding you..." : "Notify Me"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProductCard;
